import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plane, Save, RefreshCw, Loader2, AlertCircle, KeyRound } from 'lucide-react';
import { formatApiErrorDetail } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const FlightSettingsPanel = () => {
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [keyInput, setKeyInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const [s, cfg] = await Promise.all([
        axios.get(`${API}/flights/status`),
        axios.get(`${API}/admin/flight-settings`),
      ]);
      setStatus(s.data);
      setConfig(cfg.data);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!keyInput.trim()) {
      setError('Paste a FlightAware AeroAPI key first.');
      return;
    }
    setSaving(true); setError('');
    try {
      const res = await axios.put(`${API}/admin/flight-settings`, { aviationstack_key: keyInput.trim() });
      setConfig(res.data);
      setKeyInput('');
      setToast('AeroAPI key saved. Flight endpoints now use it immediately.');
      setTimeout(() => setToast(''), 3000);
      // Refresh status too
      const s = await axios.get(`${API}/flights/status`);
      setStatus(s.data);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const testKey = async () => {
    setTesting(true); setError('');
    try {
      // A cheap probe — hitting flights/status confirms the key path, then
      // a tiny flight lookup to prove the key works.
      const s = await axios.get(`${API}/flights/status`);
      setStatus(s.data);
      if (!s.data?.configured) {
        setError('No key configured yet — save one first.');
        return;
      }
      // Try a known busy airport for a real API probe.
      await axios.get(`${API}/flights/airport/KJFK`);
      setToast('AeroAPI probe succeeded — key is valid and live data is flowing.');
      setTimeout(() => setToast(''), 3500);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setTesting(false);
    }
  };

  const clearKey = async () => {
    if (!window.confirm('Clear the stored AeroAPI key from Mongo? Flights will fall back to env or demo mode.')) return;
    setSaving(true); setError('');
    try {
      const res = await axios.put(`${API}/admin/flight-settings`, { aviationstack_key: '' });
      setConfig(res.data);
      const s = await axios.get(`${API}/flights/status`);
      setStatus(s.data);
      setToast('AeroAPI key cleared.');
      setTimeout(() => setToast(''), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="glass-card rounded-2xl border border-slate-800 p-6 text-slate-500 font-mono text-xs flex items-center gap-2" data-testid="admin-flight-settings-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading flight settings…
      </section>
    );
  }

  const configured = !!status?.configured;

  return (
    <section className="glass-card rounded-2xl border border-slate-800 p-6 space-y-5" data-testid="admin-flight-settings">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Plane className="w-4 h-4 text-[#ff9e00]" />
          <h3 className="font-display font-bold text-sm text-white uppercase tracking-wide">Flight Pipeline Settings</h3>
          <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded border ${configured ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' : 'border-amber-500/40 bg-amber-500/10 text-amber-300'}`}>
            {configured ? 'LIVE · AVIATIONSTACK' : 'DEMO MODE'}
          </span>
        </div>
        <button onClick={load} className="px-2.5 py-1 rounded border border-[#ff9e00]/40 bg-[#ff9e00]/10 text-[#ff9e00] hover:bg-[#ff9e00]/20 font-mono text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 transition-all cursor-pointer" data-testid="btn-flight-settings-refresh">
          <RefreshCw className="w-3 h-3" /><span>Reload</span>
        </button>
      </div>

      {toast && <div className="rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 px-3 py-2 text-[11px] font-mono" data-testid="flight-settings-toast">{toast}</div>}
      {error && <div className="rounded border border-rose-500/30 bg-rose-500/10 text-rose-300 px-3 py-2 text-xs font-mono flex items-start gap-2" data-testid="flight-settings-error"><AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span className="break-words">{error}</span></div>}

      <div className="space-y-3">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5 mb-1.5">
            <KeyRound className="w-3 h-3" /> Aviationstack API key
            <span className="text-slate-600 ml-1">— paste to set/update</span>
          </label>
          <input
            type="password"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            placeholder={config?.aeroapi_key_masked ? `Current: ${config.aeroapi_key_masked}` : 'Paste your Aviationstack access key…'}
            className="w-full bg-slate-950/60 border border-slate-800 focus:border-[#ff9e00]/60 rounded px-3 py-2 text-sm text-slate-200 font-mono outline-none"
            data-testid="input-aeroapi-key"
            autoComplete="new-password"
          />
          <div className="flex items-center gap-2 mt-1.5 text-[10px] font-mono text-slate-500">
            <span>Current key: <span className="text-[#00f0ff]">{config?.aeroapi_key_masked || 'NOT SET'}</span></span>
            <span className={`font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded ${config?.source?.db ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/40' : config?.source?.env ? 'bg-slate-800 text-slate-400 border border-slate-700' : 'bg-slate-800 text-slate-500 border border-slate-700'}`}>
              {config?.source?.db ? 'from db' : config?.source?.env ? 'from env' : 'none'}
            </span>
          </div>
          <div className="font-mono text-[9px] text-slate-500 mt-2">
            Get your key at <a href="https://aviationstack.com/product" target="_blank" rel="noreferrer" className="text-[#ff9e00] hover:underline">aviationstack.com</a>. Free tier includes 100 requests/month. Paid plans start ~$10/mo and remove the HTTPS + real-time limitations.
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={save}
            disabled={saving || !keyInput.trim()}
            className="px-4 py-2 rounded font-mono text-[11px] uppercase tracking-widest font-bold flex items-center gap-2 bg-[#ff9e00]/15 border border-[#ff9e00]/40 text-[#ff9e00] hover:bg-[#ff9e00] hover:text-slate-950 disabled:opacity-50 transition-all cursor-pointer"
            data-testid="btn-save-aeroapi-key"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}<span>Save key</span>
          </button>
          <button
            onClick={testKey}
            disabled={testing || !configured}
            className="px-4 py-2 rounded font-mono text-[11px] uppercase tracking-widest font-bold flex items-center gap-2 bg-[#00f0ff]/10 border border-[#00f0ff]/40 text-[#00f0ff] hover:bg-[#00f0ff] hover:text-slate-950 disabled:opacity-50 transition-all cursor-pointer"
            data-testid="btn-test-aeroapi"
          >
            {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plane className="w-3.5 h-3.5" />}<span>Test connection</span>
          </button>
          {configured && config?.source?.db && (
            <button
              onClick={clearKey}
              disabled={saving}
              className="px-4 py-2 rounded font-mono text-[11px] uppercase tracking-widest font-bold flex items-center gap-2 bg-rose-500/10 border border-rose-500/30 text-rose-300 hover:bg-rose-500 hover:text-slate-950 disabled:opacity-50 transition-all cursor-pointer"
              data-testid="btn-clear-aeroapi-key"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="font-mono text-[10px] text-slate-500 border-t border-slate-900/60 pt-3 leading-relaxed">
        Same pattern as Email Settings — key persists in <code className="text-slate-300">app_settings/flights</code> in MongoDB and overrides the <code className="text-slate-300">AEROAPI_KEY</code> env var. Changes take effect on the very next request.
      </div>
    </section>
  );
};
